import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import NLPLab from './labs/NLPLab';
import NLPDeepLab from './labs/NLPDeepLab';
import { C, WS_GLOBAL_STYLE, Btn, Card, CardHead, PALETTE } from './labs/wsShared';
import { addWorkspaceItem, getAuthUserKey, readWorkspace } from './data/workspaceStore';
import { startUsageSession, usageStep, usageWorkflowFromTabs } from '../utils/usageTracker';
import PublishExperimentModal from './ui/PublishExperimentModal';
import HFDatasetPicker from './HFDatasetPicker';

let _pdf = null;
async function loadPdfJs() {
  if (_pdf) return _pdf;
  if (window.pdfjsLib) { _pdf = window.pdfjsLib; return _pdf; }
  await new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = res;
    s.onerror = () => rej(new Error('PDF.js load failed'));
    document.head.appendChild(s);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  _pdf = window.pdfjsLib;
  return _pdf;
}

async function extractPdfText(file, onProg) {
  const lib = await loadPdfJs();
  const pdf = await lib.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    pages.push(content.items.map(it => it.str).join(' ').trim());
    onProg?.(Math.round((i / pdf.numPages) * 100));
    await new Promise(r => setTimeout(r, 0));
  }
  return { text: pages.join('\n\n'), numPages: pdf.numPages, pages };
}

function readAsText(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = e => res(String(e.target.result || ''));
    fr.onerror = rej;
    fr.readAsText(file);
  });
}

function normalizeText(raw) {
  return String(raw || '')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim();
}

function splitTextToChunks(text, mode, pdfPages) {
  const t = normalizeText(text);
  if (!t) return [];

  if (mode === 'whole') return [t];

  if (mode === 'pages' && Array.isArray(pdfPages) && pdfPages.length) {
    return pdfPages.map(p => normalizeText(p)).filter(Boolean);
  }

  if (mode === 'lines') {
    return t.split('\n').map(s => s.trim()).filter(Boolean);
  }

  if (mode === 'sentences') {
    // Lightweight splitter; good enough for dashboards, not linguistics.
    const flat = t.replace(/\n+/g, ' ');
    return flat
      .split(/[.!?]\s+/g)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  // paragraphs (default)
  return t
    .split(/\n\s*\n+/g)
    .map(s => s.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function buildDataset({ fileName, text, splitMode, pdfPages }) {
  const chunks = splitTextToChunks(text, splitMode, pdfPages);
  const MAX_ROWS = 2000;
  const rows = chunks.slice(0, MAX_ROWS).map((chunk, i) => {
    const words = chunk.split(/\s+/g).filter(Boolean).length;
    return { idx: i + 1, text: chunk, chars: chunk.length, words };
  });
  return {
    name: fileName || 'document',
    headers: ['idx', 'text', 'chars', 'words'],
    rows,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB DEFINITIONS
// OVERVIEW      → document profile, chunk stats, quick insights
// CLUSTER ANALYSIS → Corpus analytics: topic models, similarity, clustering, trends
// LANGUAGE      → NLP surface linguistics: stats, freq, readability, keywords, summary
// INTELLIGENCE  → Deep NLP behavioral: sentiment, emotion, intent, signals, voice
// REPORT        → full interactive report with export
// ─────────────────────────────────────────────────────────────────────────────
const HUB_TABS = [
  { id: 'overview',      label: 'OVERVIEW',            icon: '◎', color: C.cyan   },
  { id: 'language',      label: 'LINGUISTIC ANALYSIS', icon: '◈', color: C.pink   },
  { id: 'intelligence',  label: 'BEHAVIOUR ANALYSIS',  icon: '⧉', color: C.violet },
  { id: 'data',          label: 'CLUSTER ANALYSIS',    icon: '⬡', color: C.orange },
  { id: 'report',        label: 'REPORT',              icon: '✦', color: C.green  },
];

const DOC_ANALYSIS_USAGE_WORKFLOW = usageWorkflowFromTabs(HUB_TABS);

// ─────────────────────────────────────────────────────────────────────────────
// SHARED ATOMS
// ─────────────────────────────────────────────────────────────────────────────
const Pill = ({ label, color = C.cyan, size = 8 }) => (
  <span style={{ fontSize: size, padding: '2px 7px', background: `${color}18`, border: `1px solid ${color}40`, borderRadius: 2, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</span>
);

const MetCard = ({ label, value, color = C.cyan, sub }) => (
  <div style={{ padding: '12px 14px', background: C.raised, border: `1px solid ${C.b1}`, borderLeft: `2px solid ${color}`, borderRadius: 3 }}>
    <div style={{ fontSize: 7.5, color: C.t4, letterSpacing: '.12em', fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 20, color, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{sub}</div>}
  </div>
);

const SectionHead = ({ label, color = C.cyan }) => (
  <div style={{ fontSize: 9, color, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.13em', fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ height: 1, width: 18, background: color, opacity: .5 }} />
    {label}
    <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${color}44,transparent)` }} />
  </div>
);

// NOTE MODAL ──────────────────────────────────────────────────────────────────
const NOTE_TAGS = [
  { id: 'observation', label: 'OBSERVATION', color: C.cyan },
  { id: 'finding',     label: 'FINDING',     color: C.green },
  { id: 'hypothesis',  label: 'HYPOTHESIS',  color: C.violet },
  { id: 'risk',        label: 'RISK',        color: C.red },
  { id: 'todo',        label: 'TO-DO',       color: C.gold },
];
const tagMeta = id => NOTE_TAGS.find(t => t.id === id) || NOTE_TAGS[0];

function NoteModal({ onSave, onClose }) {
  const [text, setText] = useState('');
  const [tag, setTag] = useState('observation');
  const tm = tagMeta(tag);
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(2,5,8,.88)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.b2}`, borderRadius: 6, width: 480, maxWidth: '96vw', overflow: 'hidden', boxShadow: '0 28px 70px rgba(0,0,0,.8)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
          <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: C.gold, letterSpacing: '.12em', fontWeight: 700 }}>✎ ADD NOTE TO REPORT</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.t3, cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: 5, padding: '12px 16px 8px', flexWrap: 'wrap' }}>
          {NOTE_TAGS.map(t => (
            <button key={t.id} onClick={() => setTag(t.id)} style={{ padding: '4px 10px', border: `1px solid ${tag === t.id ? t.color : C.b1}`, background: tag === t.id ? `${t.color}20` : 'transparent', color: tag === t.id ? t.color : C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 2, cursor: 'pointer' }}>{t.label}</button>
          ))}
        </div>
        <div style={{ margin: '0 16px 8px', padding: '6px 10px', borderLeft: `3px solid ${tm.color}`, background: C.raised, borderRadius: 2 }}>
          <span style={{ fontSize: 8, color: tm.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{tm.label}</span>
        </div>
        <div style={{ padding: '0 16px' }}>
          <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Your finding, observation, or to-do…" style={{ width: '100%', minHeight: 100, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: 10, fontSize: 12, fontFamily: 'inherit', borderRadius: 3, resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.75 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px' }}>
          <button onClick={onClose} style={{ padding: '6px 16px', background: 'none', border: `1px solid ${C.b1}`, color: C.t3, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}>CANCEL</button>
          <button onClick={() => { if (text.trim()) { onSave({ text: text.trim(), tag, id: `note_${Date.now()}`, ts: new Date().toISOString() }); onClose(); } }} style={{ padding: '6px 18px', background: tm.color, border: 'none', color: C.void, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700 }}>ADD TO REPORT</button>
        </div>
      </div>
    </div>
  );
}

// SUB-NAV (pill row inside a tab) ─────────────────────────────────────────────
const SubTabs = ({ active, onChange, items }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '3px 0' }}>
    {items.map(it => (
      <button
        key={it.id}
        onClick={() => onChange(it.id)}
        style={{
          padding: '5px 12px',
          borderRadius: 2,
          border: `1px solid ${active === it.id ? it.color : C.b1}`,
          background: active === it.id ? `${it.color}18` : 'transparent',
          color: active === it.id ? it.color : C.t3,
          cursor: 'pointer',
          fontSize: 9,
          fontFamily: 'JetBrains Mono,monospace',
          fontWeight: 700,
          letterSpacing: '.08em',
          transition: 'all .1s',
        }}
      >
        {it.label}
      </button>
    ))}
  </div>
);

// OVERVIEW TAB ────────────────────────────────────────────────────────────────
function OverviewTab({ ds, fileName, numPages, splitMode, rawText, onReset, onGotoLanguage, onGotoIntel, onGotoData }) {
  const totalChars = useMemo(() => ds.rows.reduce((s, r) => s + (r.chars || 0), 0), [ds]);
  const totalWords = useMemo(() => ds.rows.reduce((s, r) => s + (r.words || 0), 0), [ds]);
  const avgWords   = ds.rows.length ? Math.round(totalWords / ds.rows.length) : 0;
  const maxWords   = useMemo(() => Math.max(...ds.rows.map(r => r.words || 0)), [ds]);
  const minWords   = useMemo(() => Math.min(...ds.rows.map(r => r.words || 0)), [ds]);

  // length distribution of chunks
  const bucketCount = Math.min(20, ds.rows.length);
  const histData = useMemo(() => {
    if (!ds.rows.length) return [];
    const vals = ds.rows.map(r => r.words || 0);
    const mn = Math.min(...vals), mx = Math.max(...vals);
    if (mx === mn) return [{ bucket: mn, count: vals.length }];
    const step = Math.ceil((mx - mn + 1) / bucketCount);
    const buckets = Array.from({ length: bucketCount }, (_, i) => ({ bucket: mn + i * step, count: 0 }));
    vals.forEach(v => { const bi = Math.min(Math.floor((v - mn) / step), buckets.length - 1); buckets[bi].count++; });
    return buckets;
  }, [ds, bucketCount]);

  // top 10 chunk preview
  const preview = ds.rows.slice(0, 8);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Hero strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 10 }}>
        <MetCard label="Chunks" value={ds.rows.length.toLocaleString()} color={C.cyan} sub={`split: ${splitMode}`} />
        <MetCard label="Total Words" value={totalWords.toLocaleString()} color={C.green} />
        <MetCard label="Total Chars" value={totalChars.toLocaleString()} color={C.violet} />
        <MetCard label="Avg Words/Chunk" value={avgWords} color={C.gold} sub={`min ${minWords} · max ${maxWords}`} />
        {numPages > 0 && <MetCard label="PDF Pages" value={numPages} color={C.pink} />}
      </div>

      {/* Chunk length distribution */}
      {histData.length > 1 && (
        <div>
          <SectionHead label="CHUNK LENGTH DISTRIBUTION (WORDS)" color={C.violet} />
          <div style={{ height: 120 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={histData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.b1} vertical={false} />
                <XAxis dataKey="bucket" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={v => v} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                <Tooltip
                  content={({ active, payload }) => active && payload?.length ? (
                    <div style={{ background: C.surface, border: `1px solid ${C.b2}`, padding: '6px 10px', borderRadius: 3, fontSize: 10 }}>
                      <div style={{ color: C.t3, fontSize: 8, fontFamily: 'JetBrains Mono,monospace' }}>≥{payload[0]?.payload?.bucket} words</div>
                      <div style={{ color: C.violet }}>{payload[0]?.value} chunks</div>
                    </div>
                  ) : null}
                />
                <Bar dataKey="count" fill={C.violet} opacity={0.75} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Chunk preview table */}
      <div>
        <SectionHead label="CHUNK PREVIEW (FIRST 8)" color={C.cyan} />
        <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
            <thead>
              <tr style={{ background: C.deep }}>
                {['#', 'Text (truncated)', 'Words', 'Chars'].map(h => (
                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.07em', fontWeight: 700, borderBottom: `1px solid ${C.b1}`, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 ? `${C.raised}60` : 'transparent' }}>
                  <td style={{ padding: '6px 10px', color: C.t4, fontSize: 9, fontFamily: 'JetBrains Mono,monospace', minWidth: 30 }}>{row.idx}</td>
                  <td style={{ padding: '6px 10px', color: C.t2, maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(row.text || '').slice(0, 120)}</td>
                  <td style={{ padding: '6px 10px', color: C.cyan, fontFamily: 'JetBrains Mono,monospace' }}>{row.words}</td>
                  <td style={{ padding: '6px 10px', color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>{row.chars}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {ds.rows.length > 8 && (
          <div style={{ fontSize: 9, color: C.t4, marginTop: 5, fontFamily: 'JetBrains Mono,monospace' }}>
            Showing 8 of {ds.rows.length.toLocaleString()} chunks
          </div>
        )}
      </div>

      {/* Action row */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
        <button onClick={onGotoLanguage} style={{ padding: '10px 22px', background: `${C.pink}18`, border: `1px solid ${C.pink}`, color: C.pink, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 800, borderRadius: 4, cursor: 'pointer', letterSpacing: '.08em' }}>
          ◈ RUN LINGUISTIC ANALYSIS →
        </button>
        <button onClick={onGotoIntel} style={{ padding: '10px 22px', background: `${C.violet}18`, border: `1px solid ${C.violet}`, color: C.violet, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 800, borderRadius: 4, cursor: 'pointer', letterSpacing: '.08em' }}>
          ⧉ RUN BEHAVIOUR ANALYSIS →
        </button>
        <button onClick={onGotoData} style={{ padding: '10px 22px', background: `${C.orange}18`, border: `1px solid ${C.orange}`, color: C.orange, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 800, borderRadius: 4, cursor: 'pointer', letterSpacing: '.08em' }}>
          ⬡ EXPLORE CLUSTER ANALYSIS →
        </button>
        <button onClick={onReset} style={{ padding: '10px 18px', background: 'transparent', border: `1px solid ${C.b2}`, color: C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 700, borderRadius: 4, cursor: 'pointer', letterSpacing: '.08em', marginLeft: 'auto' }}>
          ← NEW DOCUMENT
        </button>
      </div>
    </div>
  );
}

// ── Lazy-render wrapper: only mounts children once scrolled into view ─────────
function LazySection({ children, minHeight = 120 }) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: '200px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);
  return (
    <div ref={ref} style={{ minHeight: visible ? undefined : minHeight }}>
      {visible ? children : null}
    </div>
  );
}

// ── Section wrapper (hoisted outside ReportTab so identity is stable) ─────────
function SectionWrapper({ id, label, color, secOrder, onMove, onHide, children }) {
  const pos = secOrder.indexOf(id);
  const isRequired = REPORT_SECTIONS.find(s => s.id === id)?.required;
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h2 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 16, color, margin: 0, flex: 1 }}>{label}</h2>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => onMove(id, -1)} disabled={pos <= 0} style={{ padding: '2px 8px', background: 'none', border: `1px solid ${pos > 0 ? C.b1 : C.b0}`, color: pos > 0 ? C.t3 : C.t4, borderRadius: 2, cursor: pos > 0 ? 'pointer' : 'not-allowed', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, opacity: pos > 0 ? 1 : 0.4 }}>↑</button>
          <button onClick={() => onMove(id, 1)} disabled={pos >= secOrder.length - 1} style={{ padding: '2px 8px', background: 'none', border: `1px solid ${pos < secOrder.length - 1 ? C.b1 : C.b0}`, color: pos < secOrder.length - 1 ? C.t3 : C.t4, borderRadius: 2, cursor: pos < secOrder.length - 1 ? 'pointer' : 'not-allowed', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, opacity: pos < secOrder.length - 1 ? 1 : 0.4 }}>↓</button>
          {!isRequired && (
            <button onClick={() => onHide(id)} style={{ padding: '2px 8px', background: 'none', border: `1px solid ${C.red}55`, color: C.red, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}>✕ HIDE</button>
          )}
        </div>
      </div>
      <div style={{ height: 1, background: `linear-gradient(90deg,${color}55 0%,transparent 80%)`, marginBottom: 16 }} />
      {children}
    </div>
  );
}

// REPORT TAB ──────────────────────────────────────────────────────────────────
const REPORT_SECTIONS = [
  { id: 'overview',     label: 'Document Overview',    required: true  },
  { id: 'language',     label: 'Linguistic Analysis',  required: false },
  { id: 'intelligence', label: 'Behaviour Analysis',   required: false },
  { id: 'data',         label: 'Cluster Analysis',     required: false },
  { id: 'notes',        label: 'Research Notes',       required: false },
  { id: 'conclusion',   label: 'Conclusion',           required: true  },
];

function ReportTab({ ds, fileName, numPages, splitMode, effectiveExperiment, updateExperiment }) {
  const [sections, setSections]   = useState(new Set(REPORT_SECTIONS.map(s => s.id)));
  const [secOrder, setSecOrder]   = useState(REPORT_SECTIONS.map(s => s.id));
  const [notes, setNotes]         = useState([]);
  const [noteModal, setNoteModal] = useState(false);
  const [title, setTitle]         = useState(`Document Report: ${fileName || 'Untitled'}`);
  const [langSections, setLangSections]   = useState(new Set(['stats','frequency','readability','keywords','summary']));
  const [intelSections, setIntelSections] = useState(new Set(['sentiment','deep_emotion','deep_intent','deep_signals','deep_voice','deep_annotate']));
  const [dataSections, setDataSections]   = useState(new Set(['topics','similarity','deep_clusters','deep_trends','deep_compare']));
  const reportRef = useRef();

  const LANG_REPORT_SUBS = [
    { id: 'stats',       label: 'STATS',       color: C.cyan   },
    { id: 'frequency',   label: 'FREQUENCY',   color: C.green  },
    { id: 'readability', label: 'READABILITY', color: C.pink   },
    { id: 'keywords',    label: 'KEYWORDS',    color: C.orange },
    { id: 'summary',     label: 'SUMMARY',     color: C.violet },
  ];

  const INTEL_REPORT_SUBS = [
    { id: 'sentiment',     label: 'SENTIMENT', color: C.red    },
    { id: 'deep_emotion',  label: 'EMOTION',   color: C.gold   },
    { id: 'deep_intent',   label: 'INTENT',    color: C.cyan   },
    { id: 'deep_signals',  label: 'SIGNALS',   color: C.red    },
    { id: 'deep_voice',    label: 'VOICE',     color: C.orange },
    { id: 'deep_annotate', label: 'ANNOTATE',  color: C.pink   },
  ];

  const DATA_REPORT_SUBS = [
    { id: 'topics',        label: 'TOPICS',     color: C.orange, lab: 'nlp'  },
    { id: 'similarity',    label: 'SIMILARITY', color: C.cyan,   lab: 'nlp'  },
    { id: 'deep_clusters', label: 'CLUSTERS',   color: C.violet, lab: 'deep' },
    { id: 'deep_trends',   label: 'TRENDS',     color: C.green,  lab: 'deep' },
    { id: 'deep_compare',  label: 'COMPARE',    color: C.pink,   lab: 'deep' },
  ];

  const toggleLangSec  = id => setLangSections(prev  => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleIntelSec = id => setIntelSections(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleDataSec  = id => setDataSections(prev  => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const totalWords = useMemo(() => ds.rows.reduce((s, r) => s + (r.words || 0), 0), [ds]);
  const totalChars = useMemo(() => ds.rows.reduce((s, r) => s + (r.chars || 0), 0), [ds]);
  const avgWords   = ds.rows.length ? Math.round(totalWords / ds.rows.length) : 0;

  const has = id => sections.has(id);
  const toggleSec = (id, req) => { if (req) return; setSections(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const hideSec = useCallback((id) => toggleSec(id, false), [sections]);
  const moveSection = (id, dir) => {
    setSecOrder(prev => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  };

  const addNote = n => setNotes(p => [...p, n]);
  const deleteNote = id => setNotes(p => p.filter(n => n.id !== id));
  const editNote = (id, text) => setNotes(p => p.map(n => n.id === id ? { ...n, text } : n));

  const dl = (content, type, name) => {
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([content], { type })), download: name });
    a.click(); URL.revokeObjectURL(a.href);
  };

  const exportHTML = () => {
    const body = reportRef.current?.innerHTML || '';
    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${title}</title>
<style>*{box-sizing:border-box}body{font-family:system-ui,sans-serif;background:#020508;color:#e2e8f0;padding:48px 40px;max-width:1040px;margin:0 auto;line-height:1.8}h1{font-size:22px;font-weight:800;color:#22d3ee;margin:0 0 6px}h2{font-size:14px;color:#a78bfa;margin:22px 0 8px;font-weight:800}table{border-collapse:collapse;width:100%;font-size:11px;margin:8px 0}th{background:#060b14;padding:5px 8px;text-align:left;color:#627795;border-bottom:1px solid #1e293b;font-size:8px;letter-spacing:.1em}td{padding:4px 8px;border-bottom:1px solid #0f1a2e;color:#b6c3d8}.note{background:#0a1120;border-left:3px solid #fbbf24;padding:8px 12px;margin:5px 0;border-radius:3px}.m{background:#0f1826;border-left:2px solid #22d3ee;padding:8px 12px;margin:4px 2px;border-radius:3px;display:inline-block;min-width:90px}.m .l{font-size:7px;color:#627795;letter-spacing:.1em}.m .v{font-size:18px;font-weight:800;color:#22d3ee}</style>
</head><body>${body}</body></html>`;
    dl(html, 'text/html', `${fileName || 'document'}-report.html`);
  };

  const exportMD = () => {
    let md = `# ${title}\n\n**Generated:** ${new Date().toDateString()} · ai.degenden.xyz\n\n---\n\n`;
    md += `## Document Overview\n\n`;
    md += `- **File:** \`${fileName || 'pasted text'}\`\n`;
    md += `- **Chunks:** ${ds.rows.length.toLocaleString()} (split: ${splitMode})\n`;
    md += `- **Total words:** ${totalWords.toLocaleString()}\n`;
    md += `- **Total characters:** ${totalChars.toLocaleString()}\n`;
    md += `- **Avg words/chunk:** ${avgWords}\n`;
    if (numPages) md += `- **PDF pages:** ${numPages}\n`;
    md += '\n';
    if (notes.length) {
      md += `## Research Notes\n\n`;
      notes.forEach(n => { md += `**[${n.tag.toUpperCase()}]** ${n.text}\n\n`; });
    }
    md += `## Conclusion\n\nDocument analysis of \`${fileName || 'document'}\` complete. ${ds.rows.length.toLocaleString()} chunks processed across ${totalWords.toLocaleString()} words.\n`;
    dl(md, 'text/markdown', `${fileName || 'document'}-report.md`);
  };

  if (!ds) return (
    <div style={{ padding: 48, textAlign: 'center', color: C.t4 }}>
      <div style={{ fontSize: 30, marginBottom: 8, opacity: 0.3 }}>✦</div>
      <div style={{ fontSize: 13, color: C.t3 }}>Upload a document first to generate a report</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Controls bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>TITLE:</span>
          <input value={title} onChange={e => setTitle(e.target.value)} style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '4px 8px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', minWidth: 260 }} />
        </div>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {REPORT_SECTIONS.map(s => (
            <button key={s.id} onClick={() => toggleSec(s.id, s.required)} style={{ padding: '3px 8px', border: `1px solid ${has(s.id) ? C.cyan : C.b1}`, background: has(s.id) ? `${C.cyan}15` : 'transparent', color: has(s.id) ? C.cyan : C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: s.required ? 'default' : 'pointer', opacity: s.required ? 0.6 : 1 }}>
              {has(s.id) ? '✓' : '+'} {s.label}{s.required ? ' *' : ''}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button onClick={() => setNoteModal(true)} style={{ padding: '5px 12px', border: `1px solid ${C.gold}`, background: `${C.gold}15`, color: C.gold, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 3, cursor: 'pointer' }}>✎ NOTE</button>
          <button onClick={exportMD} style={{ padding: '5px 11px', border: `1px solid ${C.cyan}`, background: `${C.cyan}15`, color: C.cyan, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 3, cursor: 'pointer' }}>↓ MD</button>
          <button onClick={exportHTML} style={{ padding: '5px 11px', border: `1px solid ${C.green}`, background: `${C.green}15`, color: C.green, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 3, cursor: 'pointer' }}>↓ HTML</button>
        </div>
      </div>

      {noteModal && <NoteModal onSave={addNote} onClose={() => setNoteModal(false)} />}

      {/* Report canvas */}
      <div ref={reportRef} style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '32px 36px', lineHeight: 1.8 }}>

        {/* Report header */}
        <h1 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 24, color: C.t1, margin: '0 0 4px' }}>{title}</h1>
        <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 22 }}>
          DOCUMENT ANALYSIS REPORT · {new Date().toLocaleDateString('en', { year: 'numeric', month: 'long', day: 'numeric' })}
          {notes.length ? ` · ${notes.length} note${notes.length > 1 ? 's' : ''}` : ''}
        </div>
        <div style={{ height: 1, background: `linear-gradient(90deg,${C.cyan}44 0%,transparent 80%)`, marginBottom: 28 }} />
        <div style={{ fontSize: 8, color: C.t4, letterSpacing: '.1em', marginBottom: 16, fontFamily: 'JetBrains Mono,monospace' }}>
          USE ↑ / ↓ BUTTONS TO REORDER SECTIONS · TOGGLE SECTIONS ABOVE
        </div>

        {secOrder.map(sid => {
          if (!has(sid)) return null;

          if (sid === 'overview') return (
            <SectionWrapper key="overview" id="overview" label="Document Overview" color={C.cyan} secOrder={secOrder} onMove={moveSection} onHide={hideSec}>
              {/* Metrics grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10, marginBottom: 20 }}>
                <MetCard label="File" value={fileName || 'Pasted'} color={C.cyan} />
                <MetCard label="Chunks" value={ds.rows.length.toLocaleString()} color={C.green} sub={`split: ${splitMode}`} />
                <MetCard label="Total Words" value={totalWords.toLocaleString()} color={C.violet} />
                <MetCard label="Total Chars" value={totalChars.toLocaleString()} color={C.gold} />
                <MetCard label="Avg Words/Chunk" value={avgWords} color={C.pink} />
                {numPages > 0 && <MetCard label="PDF Pages" value={numPages} color={C.orange} />}
              </div>

              {/* Chunk text preview */}
              <SectionHead label="FIRST 5 CHUNKS" color={C.cyan} />
              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead>
                    <tr style={{ background: C.deep }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', borderBottom: `1px solid ${C.b1}` }}>#</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', borderBottom: `1px solid ${C.b1}` }}>TEXT (truncated)</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', borderBottom: `1px solid ${C.b1}` }}>WORDS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ds.rows.slice(0, 5).map((row, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.b0}` }}>
                        <td style={{ padding: '5px 10px', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}>{row.idx}</td>
                        <td style={{ padding: '5px 10px', color: C.t2, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(row.text || '').slice(0, 140)}</td>
                        <td style={{ padding: '5px 10px', color: C.cyan, fontFamily: 'JetBrains Mono,monospace' }}>{row.words}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ds.rows.length > 5 && (
                <div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>+ {(ds.rows.length - 5).toLocaleString()} more chunks not shown</div>
              )}
            </SectionWrapper>
          );

          if (sid === 'language') return (
            <SectionWrapper key="language" id="language" label="Linguistic Analysis" color={C.pink} secOrder={secOrder} onMove={moveSection} onHide={hideSec}>
              <div style={{ marginBottom: 14, padding: '8px 12px', background: `${C.pink}09`, border: `1px solid ${C.pink}28`, borderRadius: 4, fontSize: 10, color: C.t3, lineHeight: 1.7 }}>
                <span style={{ color: C.pink, fontWeight: 700 }}>Linguistic Analysis</span> — all enabled views rendered inline. Toggle pills below to include or exclude individual sections from this report.
              </div>
              {/* Per-sub-section include toggles */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20, padding: '8px 12px', background: C.deep, borderRadius: 3, border: `1px solid ${C.b1}`, alignItems: 'center' }}>
                <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginRight: 6 }}>INCLUDE:</span>
                {LANG_REPORT_SUBS.map(sub => (
                  <button key={sub.id} onClick={() => toggleLangSec(sub.id)} style={{ padding: '3px 9px', border: `1px solid ${langSections.has(sub.id) ? sub.color : C.b1}`, background: langSections.has(sub.id) ? `${sub.color}20` : 'transparent', color: langSections.has(sub.id) ? sub.color : C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, fontWeight: 700, borderRadius: 2, cursor: 'pointer', transition: 'all .1s' }}>
                    {langSections.has(sub.id) ? '✓ ' : '+ '}{sub.label}
                  </button>
                ))}
                <button onClick={() => setLangSections(new Set(LANG_REPORT_SUBS.map(s => s.id)))} style={{ marginLeft: 'auto', padding: '3px 8px', border: `1px solid ${C.b1}`, background: 'transparent', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: 'pointer' }}>ALL</button>
                <button onClick={() => setLangSections(new Set())} style={{ padding: '3px 8px', border: `1px solid ${C.b1}`, background: 'transparent', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: 'pointer' }}>NONE</button>
              </div>
              {/* Render each enabled sub-section fully — lazy loaded */}
              {LANG_REPORT_SUBS.filter(sub => langSections.has(sub.id)).map((sub, idx, arr) => (
                <div key={sub.id} style={{ marginBottom: idx < arr.length - 1 ? 32 : 0, paddingBottom: idx < arr.length - 1 ? 24 : 0, borderBottom: idx < arr.length - 1 ? `1px solid ${C.b0}` : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}><SectionHead label={sub.label} color={sub.color} /></div>
                    <button onClick={() => toggleLangSec(sub.id)} title="Remove this section" style={{ background: 'none', border: `1px solid ${C.b1}`, color: C.red, borderRadius: 2, cursor: 'pointer', fontSize: 10, padding: '1px 7px', fontFamily: 'JetBrains Mono,monospace', lineHeight: 1.6, flexShrink: 0 }}>✕</button>
                  </div>
                  <LazySection minHeight={160}>
                    <NLPLab ds={ds} subTab={sub.id} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
                  </LazySection>
                </div>
              ))}
              {langSections.size === 0 && (
                <div style={{ padding: '28px 20px', textAlign: 'center', color: C.t4, fontSize: 11, background: C.raised, borderRadius: 4, border: `2px dashed ${C.b1}` }}>No sub-sections selected. Toggle some above to include them in the report.</div>
              )}
            </SectionWrapper>
          );

          if (sid === 'intelligence') return (
            <SectionWrapper key="intelligence" id="intelligence" label="Behaviour Analysis" color={C.violet} secOrder={secOrder} onMove={moveSection} onHide={hideSec}>
              <div style={{ marginBottom: 14, padding: '8px 12px', background: `${C.violet}09`, border: `1px solid ${C.violet}28`, borderRadius: 4, fontSize: 10, color: C.t3, lineHeight: 1.7 }}>
                <span style={{ color: C.violet, fontWeight: 700 }}>Behaviour Analysis</span> — all enabled views rendered inline. Toggle pills below to include or exclude individual sections from this report.
              </div>
              {/* Per-sub-section include toggles */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20, padding: '8px 12px', background: C.deep, borderRadius: 3, border: `1px solid ${C.b1}`, alignItems: 'center' }}>
                <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginRight: 6 }}>INCLUDE:</span>
                {INTEL_REPORT_SUBS.map(sub => (
                  <button key={sub.id} onClick={() => toggleIntelSec(sub.id)} style={{ padding: '3px 9px', border: `1px solid ${intelSections.has(sub.id) ? sub.color : C.b1}`, background: intelSections.has(sub.id) ? `${sub.color}20` : 'transparent', color: intelSections.has(sub.id) ? sub.color : C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, fontWeight: 700, borderRadius: 2, cursor: 'pointer', transition: 'all .1s' }}>
                    {intelSections.has(sub.id) ? '✓ ' : '+ '}{sub.label}
                  </button>
                ))}
                <button onClick={() => setIntelSections(new Set(INTEL_REPORT_SUBS.map(s => s.id)))} style={{ marginLeft: 'auto', padding: '3px 8px', border: `1px solid ${C.b1}`, background: 'transparent', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: 'pointer' }}>ALL</button>
                <button onClick={() => setIntelSections(new Set())} style={{ padding: '3px 8px', border: `1px solid ${C.b1}`, background: 'transparent', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: 'pointer' }}>NONE</button>
              </div>
              {/* Render each enabled sub-section fully — lazy loaded */}
              {INTEL_REPORT_SUBS.filter(sub => intelSections.has(sub.id)).map((sub, idx, arr) => (
                <div key={sub.id} style={{ marginBottom: idx < arr.length - 1 ? 32 : 0, paddingBottom: idx < arr.length - 1 ? 24 : 0, borderBottom: idx < arr.length - 1 ? `1px solid ${C.b0}` : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}><SectionHead label={sub.label} color={sub.color} /></div>
                    <button onClick={() => toggleIntelSec(sub.id)} title="Remove this section" style={{ background: 'none', border: `1px solid ${C.b1}`, color: C.red, borderRadius: 2, cursor: 'pointer', fontSize: 10, padding: '1px 7px', fontFamily: 'JetBrains Mono,monospace', lineHeight: 1.6, flexShrink: 0 }}>✕</button>
                  </div>
                  <LazySection minHeight={160}>
                    {sub.id.startsWith('deep_')
                      ? <NLPDeepLab ds={ds} subTab={sub.id} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
                      : <NLPLab     ds={ds} subTab={sub.id} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
                    }
                  </LazySection>
                </div>
              ))}
              {intelSections.size === 0 && (
                <div style={{ padding: '28px 20px', textAlign: 'center', color: C.t4, fontSize: 11, background: C.raised, borderRadius: 4, border: `2px dashed ${C.b1}` }}>No sub-sections selected. Toggle some above to include them in the report.</div>
              )}
            </SectionWrapper>
          );

          if (sid === 'data') return (
            <SectionWrapper key="data" id="data" label="Cluster Analysis" color={C.orange} secOrder={secOrder} onMove={moveSection} onHide={hideSec}>
              <div style={{ marginBottom: 14, padding: '8px 12px', background: `${C.orange}09`, border: `1px solid ${C.orange}28`, borderRadius: 4, fontSize: 10, color: C.t3, lineHeight: 1.7 }}>
                <span style={{ color: C.orange, fontWeight: 700 }}>Cluster Analysis</span> — corpus analytics for analysts, scientists and researchers: topic modelling, corpus similarity, semantic clustering, trend analysis and comparative corpus exploration.
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20, padding: '8px 12px', background: C.deep, borderRadius: 3, border: `1px solid ${C.b1}`, alignItems: 'center' }}>
                <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginRight: 6 }}>INCLUDE:</span>
                {DATA_REPORT_SUBS.map(sub => (
                  <button key={sub.id} onClick={() => toggleDataSec(sub.id)} style={{ padding: '3px 9px', border: `1px solid ${dataSections.has(sub.id) ? sub.color : C.b1}`, background: dataSections.has(sub.id) ? `${sub.color}20` : 'transparent', color: dataSections.has(sub.id) ? sub.color : C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, fontWeight: 700, borderRadius: 2, cursor: 'pointer', transition: 'all .1s' }}>
                    {dataSections.has(sub.id) ? '✓ ' : '+ '}{sub.label}
                  </button>
                ))}
                <button onClick={() => setDataSections(new Set(DATA_REPORT_SUBS.map(s => s.id)))} style={{ marginLeft: 'auto', padding: '3px 8px', border: `1px solid ${C.b1}`, background: 'transparent', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: 'pointer' }}>ALL</button>
                <button onClick={() => setDataSections(new Set())} style={{ padding: '3px 8px', border: `1px solid ${C.b1}`, background: 'transparent', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: 'pointer' }}>NONE</button>
              </div>
              {DATA_REPORT_SUBS.filter(sub => dataSections.has(sub.id)).map((sub, idx, arr) => (
                <div key={sub.id} style={{ marginBottom: idx < arr.length - 1 ? 32 : 0, paddingBottom: idx < arr.length - 1 ? 24 : 0, borderBottom: idx < arr.length - 1 ? `1px solid ${C.b0}` : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}><SectionHead label={sub.label} color={sub.color} /></div>
                    <button onClick={() => toggleDataSec(sub.id)} title="Remove this section" style={{ background: 'none', border: `1px solid ${C.b1}`, color: C.red, borderRadius: 2, cursor: 'pointer', fontSize: 10, padding: '1px 7px', fontFamily: 'JetBrains Mono,monospace', lineHeight: 1.6, flexShrink: 0 }}>✕</button>
                  </div>
                  <LazySection minHeight={160}>
                    {sub.lab === 'deep'
                      ? <NLPDeepLab ds={ds} subTab={sub.id} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
                      : <NLPLab     ds={ds} subTab={sub.id} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
                    }
                  </LazySection>
                </div>
              ))}
              {dataSections.size === 0 && (
                <div style={{ padding: '28px 20px', textAlign: 'center', color: C.t4, fontSize: 11, background: C.raised, borderRadius: 4, border: `2px dashed ${C.b1}` }}>No sub-sections selected. Toggle some above to include them in the report.</div>
              )}
            </SectionWrapper>
          );

          if (sid === 'notes') return (
            <SectionWrapper key="notes" id="notes" label="Research Notes" color={C.gold} secOrder={secOrder} onMove={moveSection} onHide={hideSec}>
              {notes.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', background: C.raised, borderRadius: 4, border: `2px dashed ${C.b1}` }}>
                  <div style={{ fontSize: 18, marginBottom: 6, opacity: 0.35 }}>✎</div>
                  <div style={{ fontSize: 11, color: C.t3, marginBottom: 3 }}>No notes yet</div>
                  <div style={{ fontSize: 10, color: C.t4 }}>Click "✎ NOTE" above to add observations, findings, or to-dos</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {notes.map((note, i) => {
                    const tm = tagMeta(note.tag);
                    return (
                      <div key={note.id} style={{ background: C.surface, border: `1px solid ${tm.color}33`, borderLeft: `3px solid ${tm.color}`, borderRadius: 3, padding: '10px 13px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <span style={{ fontSize: 8, color: tm.color, letterSpacing: '.1em', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{tm.label}</span>
                            <span style={{ fontSize: 8, color: C.t4 }}>{new Date(note.ts).toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <button onClick={() => deleteNote(note.id)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                        </div>
                        <div style={{ fontSize: 12, color: C.t1, lineHeight: 1.75 }}>{note.text}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionWrapper>
          );

          if (sid === 'conclusion') return (
            <SectionWrapper key="conclusion" id="conclusion" label="Conclusion" color={C.green} secOrder={secOrder} onMove={moveSection} onHide={hideSec}>
              <div style={{ padding: '18px 20px', background: `linear-gradient(135deg,${C.deep},#0c1620)`, border: `1px solid ${C.b1}`, borderRadius: 6 }}>
                <div style={{ fontSize: 8, letterSpacing: '.22em', color: C.cyan, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 8 }}>DOCUMENT ANALYSIS COMPLETE · ai.degenden.xyz</div>
                <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color: C.t1, marginBottom: 8 }}>{fileName || 'Document'}</div>
                <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.8 }}>
                  Analyzed <span style={{ color: C.cyan, fontWeight: 700 }}>{ds.rows.length.toLocaleString()} chunks</span> (split by {splitMode}) containing{' '}
                  <span style={{ color: C.green, fontWeight: 700 }}>{totalWords.toLocaleString()} total words</span>{' '}
                  across <span style={{ color: C.violet, fontWeight: 700 }}>{totalChars.toLocaleString()} characters</span>.
                  {numPages > 0 && <> Source: <span style={{ color: C.gold, fontWeight: 700 }}>{numPages}-page PDF</span>.</>}
                  {notes.length > 0 && <> Report includes <span style={{ color: C.gold, fontWeight: 700 }}>{notes.length} note{notes.length > 1 ? 's' : ''}</span>.</>}
                </div>
              </div>
            </SectionWrapper>
          );

          return null;
        })}
      </div>
    </div>
  );
}

function DocUploadScreen({
  splitMode,
  setSplitMode,
  rawText,
  setRawText,
  onFile,
  onRunText,
  onLoadSample,
  loading,
  status,
  progress,
  error,
}) {
  const [src, setSrc] = useState('file'); // file | paste | sample
  const [showHF, setShowHF] = useState(false);
  const [isDrag, setIsDrag] = useState(false);
  const fileRef = useRef();

  const selStyle = {
    width: '100%',
    background: C.deep,
    border: `1px solid ${C.b2}`,
    color: C.t1,
    padding: '10px 12px',
    fontSize: 11,
    fontFamily: 'JetBrains Mono,monospace',
    borderRadius: 3,
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const tabBtn = (id, label) => (
    <button
      key={id}
      onClick={() => setSrc(id)}
      style={{
        padding: '8px 14px',
        border: `1px solid ${src === id ? C.cyan : C.b1}`,
        background: src === id ? `${C.cyan}18` : 'transparent',
        color: src === id ? C.cyan : C.t3,
        fontFamily: 'JetBrains Mono,monospace',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '.08em',
        borderRadius: 4,
        cursor: 'pointer',
        transition: 'all .12s',
      }}
    >
      {label}
    </button>
  );

  const FEATURES = [
    ['📄', 'PDF + Text Upload', 'Drop PDF/TXT/MD and extract usable text instantly.'],
    ['✂️', 'Smart Chunking', 'Split into paragraphs, sentences, lines, pages, or whole-doc mode.'],
    ['🧠', 'Language Analysis', 'Stats, frequency, readability, keywords and document summarisation.'],
    ['🔬', 'Behaviour Analysis', 'Sentiment, emotion, intent, risk signals and voice-of-author.'],
    ['⬡', 'Cluster Analysis', 'Topic modelling, similarity, clustering, trends and corpus comparison.'],
    ['🧾', 'Report Builder', 'Compose a full report with notes, reorderable sections, export.'],
    ['🔒', 'Local-First', 'Runs in-browser with minimal dependencies (no server required).'],
    ['⚡', 'One Click Flow', 'Upload → Overview → Language → Intelligence → Report.'],
  ];

  const samples = [
    {
      id: 'reviews',
      icon: '💬',
      title: 'Customer Feedback Mix',
      desc: 'Good for sentiment, intent, and voice-of-customer extraction.',
      text:
        'I love the new update, but login is still slow.\n' +
        'Please fix the crash when opening settings.\n' +
        'Support was helpful, but the pricing feels confusing.\n\n' +
        'The UI looks cleaner now. However, export to CSV failed twice.\n' +
        'We need better onboarding and clearer error messages.',
    },
    {
      id: 'policy',
      icon: '📜',
      title: 'Policy / Contract Snippet',
      desc: 'Good for entities, summary, and risk signals.',
      text:
        'This agreement becomes effective on acceptance. Either party may terminate with 30 days written notice.\n' +
        'Confidential information includes customer data, pricing terms, and internal documentation.\n' +
        'In no event shall either party be liable for indirect damages except where prohibited by law.',
    },
    {
      id: 'incident',
      icon: '🛠️',
      title: 'Incident Report',
      desc: 'Good for timeline-style text and signal extraction.',
      text:
        '2026-03-10 09:12: Service latency increased in region APAC.\n' +
        '09:18: Error rate spiked for /api/upload.\n' +
        '09:26: Rolled back to previous build.\n' +
        '09:40: Metrics stabilized. Root cause suspected: misconfigured cache TTL.',
    },
  ];

  const DOC_UPLOAD_TABS = [
    { id:'upload',       label:'UPLOAD',             icon:'⬆', color:C.cyan   },
    { id:'setup',        label:'SETUP',              icon:'◉', color:C.cyan   },
    { id:'language',     label:'LINGUISTIC ANALYSIS',icon:'◈', color:C.pink   },
    { id:'intelligence', label:'BEHAVIOUR ANALYSIS', icon:'⧉', color:C.violet },
    { id:'report',       label:'REPORT',             icon:'✦', color:C.green  },
  ];

  return (
    <div style={{ width:'100%', minHeight:'100vh', background:C.void, display:'flex', flexDirection:'column', position:'relative' }}>
      {/* ── sticky top nav bar ── */}
      <div style={{ position:'sticky', top:0, zIndex:20, background:C.deep, borderBottom:`1px solid ${C.b0}`, overflow:'hidden', flexShrink:0, boxShadow:'0 6px 20px rgba(0,0,0,.35)' }}>
        <div style={{ display:'flex', flexWrap:'wrap', padding:'4px 6px', gap:2, overflowX:'hidden', maxWidth:'100%' }}>
          {DOC_UPLOAD_TABS.map(t => {
            const active = t.id === 'upload';
            return (
              <button key={t.id} disabled={!active} style={{
                display:'flex', alignItems:'center', gap:5,
                padding:'8px 11px', border:'none', background:'transparent',
                color: active ? t.color : C.t2,
                borderBottom:`2px solid ${active ? t.color : 'transparent'}`,
                fontFamily:'JetBrains Mono,monospace', fontSize:9,
                fontWeight: active ? 700 : 400,
                cursor: active ? 'default' : 'not-allowed',
                whiteSpace:'nowrap', letterSpacing:'.08em',
                transition:'all .1s', position:'relative', opacity: active ? 1 : 0.9,
              }}>
                <span style={{ fontSize:12 }}>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
         
      <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:'40px 40px 40px' }}>
        {/* bg glow */}
        <div style={{ position:'absolute', top:'20%', left:'50%', transform:'translateX(-50%)', width:760, height:360, background:`radial-gradient(ellipse,${C.violet}10 0%,transparent 70%)`, pointerEvents:'none' }} />

        <div style={{ textAlign:'center', marginBottom:40, position:'relative' }}>
          <h1 style={{ fontFamily:'Syne,sans-serif', fontWeight:900, fontSize:38, color:C.t1, lineHeight:1.1, marginBottom:12 }}>
            Analyze Docs in Minutes
            <br />
            <span style={{ background:`linear-gradient(90deg,${C.violet},${C.cyan})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              No cloud · No API keys
            </span>
          </h1>
          <p style={{ fontSize:12, color:C.t3, maxWidth:540, lineHeight:1.8, margin:'0 auto' }}>
Stats · N-grams · Frequency · Readability · Sentiment · Emotion · Reports
No coding required — just mouse clicks.
            <br />
            <span style={{ display:'inline-block', marginTop:6, padding:'4px 10px', borderRadius:999, background:`linear-gradient(90deg, ${C.violet}18, ${C.cyan}16)`, border:`1px solid ${C.violet}33`, color:C.t1 }}>
              Your data never leaves your machine. 100% on Browser &amp; 100% Privacy.
            </span>
          </p>
        </div>

        <div style={{ width:'100%', maxWidth:720, background:C.surface, border:`1px solid ${C.b1}`, borderRadius:8, overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,.6)', position:'relative', zIndex:1 }}>
          {/* tab bar — underline style matching AutoEDA */}
          <div style={{ display:'flex', background:C.deep, borderBottom:`1px solid ${C.b0}` }}>
            {[['file','📁 Upload File'],['hf','🤗 HuggingFace'],['paste','✍️ Paste Text'],['sample','⬟ Sample Docs']].map(([id,lbl])=>(
              <button key={id} onClick={()=>{ if(id==='hf'){setShowHF(true);return;} setSrc(id); }} style={{ flex:1, padding:'14px', border:'none', background:(id==='hf'?showHF:src===id)?C.surface:'transparent', color:(id==='hf'?showHF:src===id)?C.violet:C.t3, fontFamily:'JetBrains Mono,monospace', fontSize:9, fontWeight:700, cursor:'pointer', borderBottom:`2px solid ${(id==='hf'?showHF:src===id)?C.violet:'transparent'}`, transition:'all .12s' }}>{lbl}</button>
            ))}
          </div>

          <div style={{ padding:30 }}>
            {src==='file' && (
              <>
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDrag(true); }}
                  onDragLeave={() => setIsDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setIsDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
                  onClick={() => fileRef.current?.click()}
                  style={{ border:`2px dashed ${isDrag?C.violet:C.b2}`, borderRadius:8, padding:'60px 20px', textAlign:'center', cursor:loading?'wait':'pointer', background:isDrag?`${C.violet}07`:C.deep, transition:'all .2s' }}
                >
                  <div style={{ fontSize:52, marginBottom:12, opacity:.5 }}>{loading?'⏳':'📄'}</div>
                  <div style={{ fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:17, color:C.t1, marginBottom:6 }}>
                    {loading ? (status||'Loading...') : 'Drop PDF / TXT / MD here'}
                  </div>
                  <div style={{ fontSize:11, color:C.t3 }}>{loading ? 'Extracting text…' : 'or click to browse · PDF / TXT / MD'}</div>
                  <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.tex,.json" style={{ display:'none' }} onChange={(e) => onFile(e.target.files?.[0])} />
                </div>
                <div style={{ marginTop:14, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <div style={{ fontSize:8.5, color:C.t4, fontFamily:'JetBrains Mono,monospace', letterSpacing:'.12em', flexShrink:0 }}>SPLIT MODE</div>
                  <select value={splitMode} onChange={(e) => setSplitMode(e.target.value)} style={{ ...selStyle, width:'auto', flex:1 }}>
                    <option value="paragraphs">Paragraphs (Recommended)</option>
                    <option value="sentences">Sentences</option>
                    <option value="lines">Lines</option>
                    <option value="whole">Whole document (1 row)</option>
                    <option value="pages">Pages (PDF only)</option>
                  </select>
                  {loading && <div style={{ fontSize:10, color:C.t3, fontFamily:'JetBrains Mono,monospace' }}>{progress?`${progress}%`:''}</div>}
                </div>
              </>
            )}

            {src==='paste' && (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder="Paste any unstructured text here (logs, notes, reports, research paper text)..."
                  style={{ width:'100%', minHeight:180, background:C.deep, border:`1px solid ${C.b2}`, color:C.t1, padding:'10px 12px', fontSize:12, fontFamily:'JetBrains Mono,monospace', borderRadius:6, outline:'none', resize:'vertical', boxSizing:'border-box', lineHeight:1.7 }}
                />
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ fontSize:10, color:C.t4 }}>Tip: start with Paragraphs split mode for best results.</div>
                  <button
                    onClick={onRunText}
                    disabled={!rawText.trim()||loading}
                    style={{ padding:'10px 18px', background:!rawText.trim()||loading?C.b1:C.violet, border:`1px solid ${!rawText.trim()||loading?C.b2:C.violet}`, color:!rawText.trim()||loading?C.t4:C.void, fontSize:10, cursor:!rawText.trim()||loading?'not-allowed':'pointer', borderRadius:6, fontFamily:'JetBrains Mono,monospace', letterSpacing:'.08em', fontWeight:800, transition:'all .12s' }}
                  >
                    RUN ANALYSIS →
                  </button>
                </div>
              </div>
            )}

            {src==='sample' && (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ fontSize:11, color:C.t2, marginBottom:4 }}>Click a sample to load it instantly:</div>
                {samples.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => onLoadSample(s)}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:C.surface, border:`1px solid ${C.b1}`, borderRadius:8, cursor:'pointer', transition:'border-color .12s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.violet; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.b1; }}
                  >
                    <div style={{ fontSize:20, opacity:0.7 }}>{s.icon}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:11, color:C.t1, fontWeight:800 }}>{s.title}</div>
                      <div style={{ fontSize:10, color:C.t3, marginTop:2, lineHeight:1.5 }}>{s.desc}</div>
                    </div>
                    <div style={{ fontSize:9, color:C.violet, fontFamily:'JetBrains Mono,monospace', fontWeight:800 }}>LOAD →</div>
                  </div>
                ))}
              </div>
            )}

            {showHF && (
              <HFDatasetPicker
                accentColor={C.violet}
                textMode={true}
                onClose={() => setShowHF(false)}
                onLoaded={(text, name) => {
                  const syntheticFile = new File([text], name.replace('.csv', '.txt'), { type: 'text/plain' });
                  onFile(syntheticFile);
                }}
              />
            )}

            {error && <div style={{ marginTop:12, padding:'9px 14px', background:`${C.red}10`, border:`1px solid ${C.red}40`, borderRadius:4, fontSize:10, color:C.red }}>{error}</div>}
          </div>
        </div>

        {/* Feature grid — 4-col matching AutoEDA */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginTop:28, maxWidth:720, width:'100%' }}>
          {FEATURES.map(([icon, label, desc]) => (
            <div key={label} style={{ padding:'12px', background:C.surface, border:`1px solid ${C.b1}`, borderRadius:5 }}>
              <div style={{ fontSize:16, marginBottom:5 }}>{icon}</div>
              <div style={{ fontSize:10, color:C.t1, fontWeight:600, marginBottom:3 }}>{label}</div>
              <div style={{ fontSize:8.5, color:C.t4, lineHeight:1.5 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export default function DocumentAnalysisHub({ onBack, isAuthenticated, authUser }) {
  const [tab, setTab] = useState('overview');
  const [langSub, setLangSub] = useState('setup');
  const [intelSub, setIntelSub] = useState('deep_setup');
  const [dataSub, setDataSub] = useState('deep_clusters');
  const [publishOpen, setPublishOpen] = useState(false);

  const [fileName, setFileName] = useState('');
  const [rawText, setRawText] = useState('');
  const [pdfPages, setPdfPages] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [splitMode, setSplitMode] = useState('paragraphs');

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const [ds, setDs] = useState(null);
  const userKey = getAuthUserKey(authUser);
  const [experiment, setExperiment] = useState(() => ({
    id: Date.now(),
    name: 'Document Analysis',
    nlpConfig: { primaryCol: 'text', secondaryCol: '', useStopwords: true, sampleSize: 1000 },
    deepNlpConfig: { primaryCol: 'text', groupCol: '', dateCol: '', sampleSize: 1000, useStopwords: true },
  }));

  const updateExperiment = useCallback((patch) => {
    setExperiment(prev => ({ ...prev, ...patch }));
  }, []);

  const effectiveExperiment = useMemo(() => ({
    ...experiment,
    name: fileName || experiment.name,
  }), [experiment, fileName]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setLoading(true);
    setError('');
    setProgress(0);
    setStatus('Loading...');
    setFileName(file.name);
    setNumPages(0);
    setPdfPages(null);

    try {
      let txt = '';
      let pages = null;
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        setStatus('Extracting PDF text...');
        const r = await extractPdfText(file, (p) => {
          setProgress(p);
          setStatus(`Extracting PDF... ${p}%`);
        });
        txt = r.text;
        setNumPages(r.numPages || 0);
        pages = r.pages || null;
        setPdfPages(pages);
      } else {
        setStatus('Reading text...');
        txt = await readAsText(file);
      }

      const cleaned = normalizeText(txt);
      setRawText(cleaned);
      const next = buildDataset({ fileName: file.name, text: cleaned, splitMode, pdfPages: isPdf ? pages : null });
      setDs(next);

      startUsageSession('doc_analysis');
      usageStep('doc_analysis', 'upload', { name: file.name, chunks: next?.rows?.length || 0 }, DOC_ANALYSIS_USAGE_WORKFLOW);

      if (isAuthenticated) {
        addWorkspaceItem(userKey, 'doc_analysis', {
          kind: 'run',
          title: file.name,
          meta: {
            chunks: next?.rows?.length || 0,
            splitMode,
            numPages: isPdf ? (pages?.length || 0) : 0,
          },
          payload: {
            fileName: file.name,
            splitMode,
            rawText: cleaned.slice(0, 200000),
          },
        });
      }

      setTab('overview');
      setLangSub('setup');
      setIntelSub('deep_setup');
      setDataSub('deep_clusters');
    } catch (e) {
      setError(e?.message || 'Failed to load document');
    } finally {
      setLoading(false);
      setStatus('');
      setProgress(0);
    }
  }, [splitMode, isAuthenticated, userKey]);

  const doReset = useCallback(() => {
    setRawText('');
    setDs(null);
    setFileName('');
    setNumPages(0);
    setPdfPages(null);
    setError('');
    setTab('overview');
    setLangSub('setup');
    setIntelSub('deep_setup');
    setDataSub('deep_clusters');
  }, []);

  const buildSnapshot = useCallback(() => ({
    dataset: {
      name: fileName || 'document',
      chunks: ds?.rows?.length || 0,
      split_mode: splitMode,
      num_pages: numPages || 0,
      total_words: ds?.rows?.reduce((s, r) => s + (r.words || 0), 0) || 0,
      total_chars: ds?.rows?.reduce((s, r) => s + (r.chars || 0), 0) || 0,
    },
    tabs_completed: HUB_TABS.map(t => t.id),
  }), [ds, fileName, splitMode, numPages]);

  const ingestText = useCallback(({
    title,
    text,
    splitMode: splitOverride,
    save = true,
  }) => {
    const cleaned = normalizeText(text);
    const name = title || 'document';
    const mode = splitOverride || splitMode;

    setError('');
    setFileName(name);
    setSplitMode(mode);
    setRawText(cleaned);
    setPdfPages(null);
    setNumPages(0);

    const next = buildDataset({ fileName: name, text: cleaned, splitMode: mode, pdfPages: null });
    setDs(next);

    startUsageSession('doc_analysis');
    usageStep('doc_analysis', 'upload', { name, chunks: next?.rows?.length || 0 }, DOC_ANALYSIS_USAGE_WORKFLOW);

    if (save && isAuthenticated) {
      addWorkspaceItem(userKey, 'doc_analysis', {
        kind: 'run',
        title: name,
        meta: { chunks: next?.rows?.length || 0, splitMode: mode, numPages: 0 },
        payload: { fileName: name, splitMode: mode, rawText: cleaned.slice(0, 200000) },
      });
    }

    setTab('overview');
    setLangSub('setup');
    setIntelSub('deep_setup');
    setDataSub('deep_clusters');
  }, [splitMode, isAuthenticated, userKey]);

  useEffect(() => {
    const onNew = () => doReset();
    const onOpen = (ev) => {
      const id = ev?.detail?.id;
      if (!id) return;
      const ws = readWorkspace(userKey);
      const items = ws?.modules?.doc_analysis?.items || [];
      const it = items.find(x => x && x.id === id);
      const payload = it?.payload;
      if (!payload?.rawText) return;
      ingestText({
        title: payload.fileName || it.title || 'document',
        text: payload.rawText,
        splitMode: payload.splitMode || 'paragraphs',
        save: false,
      });
    };
    const onLoadText = (ev) => {
      const text = ev?.detail?.text;
      if (!text) return;
      const title = ev?.detail?.title || 'sample.txt';
      ingestText({ title, text: String(text), splitMode: 'paragraphs', save: false });
    };
    window.addEventListener('dd:doc_analysis:new', onNew);
    window.addEventListener('dd:doc_analysis:open', onOpen);
    window.addEventListener('dd:doc_analysis:load_text', onLoadText);
    return () => {
      window.removeEventListener('dd:doc_analysis:new', onNew);
      window.removeEventListener('dd:doc_analysis:open', onOpen);
      window.removeEventListener('dd:doc_analysis:load_text', onLoadText);
    };
  }, [doReset, userKey, ingestText]);

  useEffect(() => {
    if (tab === 'language')     usageStep('doc_analysis', 'language',     null, DOC_ANALYSIS_USAGE_WORKFLOW);
    if (tab === 'intelligence') usageStep('doc_analysis', 'intelligence', null, DOC_ANALYSIS_USAGE_WORKFLOW);
    if (tab === 'data')         usageStep('doc_analysis', 'data',         null, DOC_ANALYSIS_USAGE_WORKFLOW);
    if (tab === 'report')       usageStep('doc_analysis', 'report',       null, DOC_ANALYSIS_USAGE_WORKFLOW);
  }, [tab]);

  const LANG_SUBTABS = [
    { id: 'setup',       label: 'SETUP',       color: C.pink   },
    { id: 'stats',       label: 'STATS',       color: C.cyan   },
    { id: 'frequency',   label: 'FREQ',        color: C.green  },
    { id: 'readability', label: 'READABILITY', color: C.pink   },
    { id: 'keywords',    label: 'KEYWORDS',    color: C.orange },
    { id: 'summary',     label: 'SUMMARY',     color: C.violet },
  ];

  const INTEL_SUBTABS = [
    { id: 'deep_setup',    label: 'SETUP',     color: C.violet },
    { id: 'sentiment',     label: 'SENTIMENT', color: C.red    },
    { id: 'deep_emotion',  label: 'EMOTION',   color: C.gold   },
    { id: 'deep_intent',   label: 'INTENT',    color: C.cyan   },
    { id: 'deep_signals',  label: 'SIGNALS',   color: C.red    },
    { id: 'deep_voice',    label: 'VOICE',     color: C.orange },
    { id: 'deep_annotate', label: 'ANNOTATE',  color: C.pink   },
  ];

  const DATA_SUBTABS = [
    { id: 'topics',        label: 'TOPICS',     color: C.orange },
    { id: 'similarity',    label: 'SIMILARITY', color: C.cyan   },
    { id: 'deep_clusters', label: 'CLUSTERS',   color: C.violet },
    { id: 'deep_trends',   label: 'TRENDS',     color: C.green  },
    { id: 'deep_compare',  label: 'COMPARE',    color: C.pink   },
  ];

  // No document loaded — show the full-page upload hero
  if (!ds) {
    return (
      <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: C.void }}>
        <style>{WS_GLOBAL_STYLE}</style>
        <DocUploadScreen
          splitMode={splitMode}
          setSplitMode={setSplitMode}
          rawText={rawText}
          setRawText={setRawText}
          onFile={handleFile}
          onRunText={() => ingestText({ title: fileName || 'Pasted text', text: rawText, splitMode, save: true })}
          onLoadSample={(s) => ingestText({ title: s.title, text: s.text, splitMode: 'paragraphs', save: false })}
          loading={loading}
          status={status}
          progress={progress}
          error={error}
        />
      </div>
    );
  }

  // Document loaded — sticky TopBar + tab content
  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: C.void }}>
      <style>{WS_GLOBAL_STYLE}</style>

      {/* ── Sticky TopBar ── */}
      <div style={{ background: C.deep, borderBottom: `1px solid ${C.b0}`, flexShrink: 0 }}>
        {/* File info strip */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 16px', borderBottom: `1px solid ${C.b0}`,
          flexWrap: 'wrap',
        }}>
          <button
            onClick={doReset}
            style={{
              padding: '4px 10px', border: `1px solid ${C.b2}`, background: 'transparent',
              color: C.t3, fontSize: 9, fontFamily: 'JetBrains Mono,monospace',
              fontWeight: 700, letterSpacing: '.08em', borderRadius: 3, cursor: 'pointer',
            }}
          >
            ← NEW
          </button>
          <span style={{ fontSize: 10, color: C.t2, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fileName}
          </span>
          <Pill label={`${ds.rows.length} chunks`} color={C.cyan} />
          <Pill label={splitMode} color={C.violet} />
          {numPages > 0 && <Pill label={`${numPages} pages`} color={C.gold} />}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {isAuthenticated && (
              <button
                onClick={() => setPublishOpen(true)}
                style={{
                  padding: '4px 10px', border: `1px solid ${C.violet}`, background: `${C.violet}18`,
                  color: C.violet, fontSize: 9, fontFamily: 'JetBrains Mono,monospace',
                  fontWeight: 700, letterSpacing: '.08em', borderRadius: 3, cursor: 'pointer',
                }}
              >
                📤 PUBLISH
              </button>
            )}
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  padding: '4px 10px', border: `1px solid ${C.b2}`, background: 'transparent',
                  color: C.t4, fontSize: 9, fontFamily: 'JetBrains Mono,monospace',
                  fontWeight: 700, letterSpacing: '.08em', borderRadius: 3, cursor: 'pointer',
                }}
              >
                BACK
              </button>
            )}
          </div>
        </div>

        {/* Tab row — bottom-border underline style */}
        <div style={{ display: 'flex', overflowX: 'auto', padding: '0 6px', gap: 0 }}>
          {HUB_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '10px 18px',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${tab === t.id ? t.color : 'transparent'}`,
                color: tab === t.id ? t.color : C.t3,
                fontFamily: 'JetBrains Mono,monospace',
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: '.1em',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color .12s, border-color .12s',
              }}
            >
              {t.icon}&nbsp;&nbsp;{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '20px 16px' }}>
        {tab === 'overview' && (
          <OverviewTab
            ds={ds}
            fileName={fileName}
            numPages={numPages}
            splitMode={splitMode}
            rawText={rawText}
            onReset={doReset}
            onGotoLanguage={() => setTab('language')}
            onGotoIntel={() => setTab('intelligence')}
            onGotoData={() => setTab('data')}
          />
        )}

        {tab === 'language' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SubTabs active={langSub} onChange={setLangSub} items={LANG_SUBTABS} />
            <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 6, padding: 12 }}>
              <NLPLab ds={ds} subTab={langSub} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
            </div>
          </div>
        )}

        {tab === 'intelligence' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SubTabs active={intelSub} onChange={setIntelSub} items={INTEL_SUBTABS} />
            <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 6, padding: 12 }}>
              {intelSub.startsWith('deep_')
                ? <NLPDeepLab ds={ds} subTab={intelSub} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
                : <NLPLab     ds={ds} subTab={intelSub} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
              }
            </div>
          </div>
        )}

        {tab === 'data' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SubTabs active={dataSub} onChange={setDataSub} items={DATA_SUBTABS} />
            <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 6, padding: 12 }}>
              {dataSub.startsWith('deep_')
                ? <NLPDeepLab ds={ds} subTab={dataSub} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
                : <NLPLab     ds={ds} subTab={dataSub} experiment={effectiveExperiment} onUpdate={updateExperiment} dashboardCards={[]} />
              }
            </div>
          </div>
        )}

        {tab === 'report' && (
          <ReportTab
            ds={ds}
            fileName={fileName}
            numPages={numPages}
            splitMode={splitMode}
            effectiveExperiment={effectiveExperiment}
            updateExperiment={updateExperiment}
          />
        )}
      </div>
      {publishOpen && (
        <PublishExperimentModal
          snapshot={buildSnapshot()}
          expType="doc_analysis"
          onClose={() => setPublishOpen(false)}
          onPublished={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}
